import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, MessageSquare, Copy, Pencil } from "lucide-react";

// WhatsApp/Instagram message templates (Meta HSM). Required to reach a customer
// outside the 24h free-form window. I.e. every dormant winback, and any
// workflow follow-up sent a day or more after the customer last wrote. Meta
// approval is out-of-band: the owner authors the body here, pastes it into
// Business Manager, then flips the status once Meta clears it. The runtime
// senders read the approved row to send the real template (win-backs:
// dormantWinbackFromSegment.ts; workflow Send message steps, which name their
// template: workflowTemplate.ts).

type Channel = "whatsapp" | "instagram";
type Purpose = "winback_offer" | "winback_soft" | "workflow";
type Status = "draft" | "submitted" | "approved" | "rejected" | "paused";
type HeaderFormat = "none" | "image";

interface TemplateRow {
  id: string;
  channel: Channel;
  purpose: Purpose;
  provider_template_name: string;
  provider_template_lang: string;
  status: Status;
  body_text: string | null;
  body_var_order: string[];
  header_text: string | null;
  header_format: HeaderFormat;
  footer_text: string | null;
  status_note: string | null;
  /** Made and submitted to Meta by Dibs from a workflow step; Meta's review sets its status. */
  managed: boolean;
}

interface Draft {
  id: string | null; // null = new
  channel: Channel;
  purpose: Purpose;
  provider_template_name: string;
  provider_template_lang: string;
  body: string; // authored with {token} placeholders
  header_format: HeaderFormat;
  footer_text: string;
}

interface Token {
  token: string;
  key: string;
  label: string;
  sample: string;
}

// The structured WinbackCopy fields the AI generates, exposed as friendly tokens
// the owner drops into the body. Order of appearance becomes {{1}}..{{n}}.
const WINBACK_TOKENS: Token[] = [
  { token: "{greeting}", key: "greeting", label: "Greeting", sample: "Hi Sarah," },
  {
    token: "{personalized_line}",
    key: "personalized_line",
    label: "Personal line",
    sample: "It's been a while since your last visit — we'd love to welcome you back.",
  },
  { token: "{offer}", key: "offer_line", label: "Offer", sample: "As a welcome-back, enjoy 15% off your next visit." },
  { token: "{cta}", key: "cta", label: "Next step", sample: "Just reply and we'll find a time that works." },
];

// A workflow's Send message step fills these from the customer the run is
// for, exactly like the {{customer_name}} tokens in the step's own text.
// Keys match WORKFLOW_TEMPLATE_TOKENS in workflowTemplate.ts.
const WORKFLOW_TOKENS: Token[] = [
  { token: "{customer_name}", key: "customer_name", label: "Customer name", sample: "Sarah" },
  { token: "{listing_name}", key: "listing_name", label: "Service", sample: "Trial class" },
  { token: "{booking_date}", key: "booking_date", label: "Booking date", sample: "Sat 12 Oct" },
  { token: "{booking_time}", key: "booking_time", label: "Booking time", sample: "3:00 PM" },
];

const tokensFor = (purpose: Purpose) => (purpose === "workflow" ? WORKFLOW_TOKENS : WINBACK_TOKENS);

const tokenRe = (tokens: Token[]) =>
  new RegExp(`\\{(${tokens.map((t) => t.token.slice(1, -1)).join("|")})\\}`, "g");

const DEFAULT_BODY: Record<Purpose, string> = {
  winback_offer: "{greeting}\n\n{personalized_line}\n\n{offer}\n\n{cta}",
  winback_soft: "{greeting}\n\n{personalized_line}\n\n{offer}\n\n{cta}",
  workflow: "Hi {customer_name}, just following up on my last message. Let me know if you have any questions.",
};

const STATUS_BADGE: Record<Status, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  submitted: { label: "Submitted to Meta", className: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved · live", className: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-800" },
  paused: { label: "Paused", className: "bg-slate-200 text-slate-700" },
};

const PURPOSE_LABEL: Record<Purpose, string> = {
  winback_offer: "With an offer",
  winback_soft: "No offer (soft)",
  workflow: "Workflow message",
};

/** Authored token-body → Meta placeholder body + the ordered var→copy-key map. */
function buildMeta(body: string, tokens: Token[]): { metaBody: string; varOrder: string[] } {
  let n = 0;
  const varOrder: string[] = [];
  const metaBody = body.replace(tokenRe(tokens), (m) => {
    n += 1;
    varOrder.push(tokens.find((t) => t.token === m)?.key ?? "");
    return `{{${n}}}`;
  });
  return { metaBody, varOrder };
}

/** Meta placeholder body (stored) → authored token-body, for re-editing. */
function metaToTokens(metaBody: string, varOrder: string[], tokens: Token[]): string {
  return metaBody.replace(
    /\{\{(\d+)\}\}/g,
    (_m, d) => tokens.find((t) => t.key === varOrder[Number(d) - 1])?.token ?? `{{${d}}}`,
  );
}

/** Preview: token-body rendered with sample copy. */
function renderSample(body: string, tokens: Token[]): string {
  return body.replace(tokenRe(tokens), (m) => tokens.find((t) => t.token === m)?.sample ?? "");
}

function emptyDraft(): Draft {
  return {
    id: null,
    channel: "whatsapp",
    purpose: "winback_offer",
    provider_template_name: "",
    provider_template_lang: "en",
    body: DEFAULT_BODY.winback_offer,
    header_format: "none",
    footer_text: "",
  };
}

const WinbackTemplates = () => {
  const { businessId } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("business_message_templates")
      .select(
        "id, channel, purpose, provider_template_name, provider_template_lang, status, body_text, body_var_order, header_text, header_format, footer_text, status_note, managed",
      )
      .eq("business_id", businessId)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Couldn't load templates", description: error.message, variant: "destructive" });
    } else {
      setRows(
        (data ?? []).map((r) => ({
          ...r,
          body_var_order: Array.isArray(r.body_var_order) ? (r.body_var_order as string[]) : [],
          header_format: r.header_format === "image" ? "image" : "none",
        })) as TemplateRow[],
      );
    }
    setIsLoading(false);
  }, [businessId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const insertToken = (token: string) => {
    setDraft((d) => {
      if (!d) return d;
      const el = bodyRef.current;
      if (!el) return { ...d, body: `${d.body}${token}` };
      const start = el.selectionStart ?? d.body.length;
      const end = el.selectionEnd ?? d.body.length;
      const next = d.body.slice(0, start) + token + d.body.slice(end);
      // Restore caret after the inserted token on next tick.
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return { ...d, body: next };
    });
  };

  // Switching between a win-back and a workflow template swaps the untouched
  // starter body; anything the owner wrote is kept.
  const setPurpose = (purpose: Purpose) => {
    setDraft((d) => {
      if (!d) return d;
      const body = d.body === DEFAULT_BODY[d.purpose] ? DEFAULT_BODY[purpose] : d.body;
      return purpose === "workflow"
        ? { ...d, purpose, body, channel: "whatsapp", header_format: "none" }
        : { ...d, purpose, body };
    });
  };

  const startEdit = (r: TemplateRow) => {
    setDraft({
      id: r.id,
      channel: r.channel,
      purpose: r.purpose,
      provider_template_name: r.provider_template_name,
      provider_template_lang: r.provider_template_lang,
      body: r.body_text ? metaToTokens(r.body_text, r.body_var_order, tokensFor(r.purpose)) : "",
      header_format: r.header_format,
      footer_text: r.footer_text ?? "",
    });
  };

  const saveDraft = async () => {
    if (!businessId || !draft) return;
    if (!draft.provider_template_name.trim()) {
      toast({ title: "Enter the template name from Meta", variant: "destructive" });
      return;
    }
    const isWorkflow = draft.purpose === "workflow";
    const { metaBody, varOrder } = buildMeta(draft.body, tokensFor(draft.purpose));
    if (!metaBody.trim()) {
      toast({ title: "Write the message body", variant: "destructive" });
      return;
    }
    // A workflow template may be fixed text; a win-back needs the AI's copy.
    if (!isWorkflow && varOrder.length === 0) {
      toast({
        title: "Add at least one variable",
        description: "Drop in {greeting}, {personalized_line}, {offer}, or {cta} so the AI can personalize.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    const row = {
      business_id: businessId,
      // Workflows send templates on WhatsApp only, with no image header.
      channel: isWorkflow ? "whatsapp" : draft.channel,
      purpose: draft.purpose,
      provider_template_name: draft.provider_template_name.trim(),
      provider_template_lang: draft.provider_template_lang.trim() || "en",
      body_text: metaBody,
      body_var_order: varOrder,
      header_format: isWorkflow ? "none" : draft.header_format,
      footer_text: draft.footer_text.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = draft.id
      ? await supabase.from("business_message_templates").update(row).eq("id", draft.id)
      : await supabase.from("business_message_templates").insert({ ...row, status: "draft" });

    setIsSaving(false);
    if (error) {
      toast({ title: "Couldn't save template", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: draft.id ? "Template updated" : "Template saved as draft" });
    setDraft(null);
    load();
  };

  const setStatus = async (r: TemplateRow, status: Status) => {
    setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    const { error } = await supabase
      .from("business_message_templates")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", r.id);
    if (error) {
      toast({ title: "Couldn't update status", description: error.message, variant: "destructive" });
      load();
    }
  };

  const remove = async (r: TemplateRow) => {
    const { error } = await supabase.from("business_message_templates").delete().eq("id", r.id);
    if (error) {
      toast({ title: "Couldn't delete template", description: error.message, variant: "destructive" });
      return;
    }
    setRows((prev) => prev.filter((x) => x.id !== r.id));
    toast({ title: "Template deleted" });
  };

  const copyForMeta = (r: TemplateRow) => {
    navigator.clipboard?.writeText(r.body_text ?? "");
    toast({ title: "Body copied", description: "Paste it into the Meta Business Manager template editor." });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-sm font-bold uppercase tracking-wider text-foreground">WhatsApp message templates</p>
        <p className="text-sm text-muted-foreground leading-snug">
          WhatsApp only lets you message a customer freely for 24 hours after they last wrote to you. A win-back to a
          dormant customer, or a workflow follow-up sent days later, falls outside that window, so it must use a
          template you've registered and Meta has approved. Author the body here, paste it into Meta Business
          Manager, then mark it approved once Meta clears it.
        </p>
      </div>

      {/* Existing templates */}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No templates yet. Add one below so win-backs and workflow follow-ups can reach customers on WhatsApp.
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const badge = STATUS_BADGE[r.status];
            return (
              <div key={r.id} className="border border-border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {r.provider_template_name}{" "}
                        <span className="text-muted-foreground font-normal">· {r.provider_template_lang}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {r.channel} · {PURPOSE_LABEL[r.purpose]}
                        {r.managed ? " · Managed by Dibs" : ""}
                      </p>
                      {r.body_text && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                          {r.body_text}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge className={`shrink-0 ${badge.className}`}>{badge.label}</Badge>
                </div>
                {r.managed && r.status_note && (
                  <p className="text-xs text-muted-foreground">{r.status_note}</p>
                )}

                {!r.managed && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => copyForMeta(r)}>
                    <Copy className="h-3.5 w-3.5" /> Copy for Meta
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => startEdit(r)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  {/* Status lifecycle — manual, mirrors out-of-band Meta review. */}
                  {r.status === "draft" && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setStatus(r, "submitted")}>
                      Mark submitted
                    </Button>
                  )}
                  {(r.status === "submitted" || r.status === "rejected" || r.status === "paused") && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setStatus(r, "approved")}>
                      Mark approved
                    </Button>
                  )}
                  {r.status === "submitted" && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setStatus(r, "rejected")}>
                      Mark rejected
                    </Button>
                  )}
                  {r.status === "approved" && (
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setStatus(r, "paused")}>
                      Pause
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-auto text-muted-foreground hover:text-destructive"
                    onClick={() => remove(r)}
                    aria-label="Delete template"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add / edit form */}
      {draft ? (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Channel</Label>
              <Select
                value={draft.channel}
                onValueChange={(v) => setDraft({ ...draft, channel: v as Channel })}
                disabled={draft.purpose === "workflow"}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Use for</Label>
              <Select value={draft.purpose} onValueChange={(v) => setPurpose(v as Purpose)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="winback_offer">Win-backs that include an offer</SelectItem>
                  <SelectItem value="winback_soft">Win-backs with no offer</SelectItem>
                  <SelectItem value="workflow">A workflow's Send message step</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Template name (exactly as in Meta)</Label>
              <Input
                value={draft.provider_template_name}
                onChange={(e) => setDraft({ ...draft, provider_template_name: e.target.value })}
                placeholder={draft.purpose === "workflow" ? "e.g. follow_up_1" : "e.g. winback_offer_en"}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Language code</Label>
              <Input
                value={draft.provider_template_lang}
                onChange={(e) => setDraft({ ...draft, provider_template_lang: e.target.value })}
                placeholder="en"
              />
            </div>
          </div>

          {draft.purpose === "workflow" ? (
            <p className="text-xs text-muted-foreground">
              Pick this template on a workflow's Send message step. The step sends its own message while the customer's
              24-hour window is open, and this template once it has closed.
            </p>
          ) : (
          <div className="space-y-1.5">
            <Label className="text-sm">Header</Label>
            <Select
              value={draft.header_format}
              onValueChange={(v) => setDraft({ ...draft, header_format: v as HeaderFormat })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (text only)</SelectItem>
                <SelectItem value="image">Image</SelectItem>
              </SelectContent>
            </Select>
            {draft.header_format === "image" && (
              <p className="text-xs text-muted-foreground">
                Create this template in Meta Business Manager with an <strong>Image</strong> header and a sample image,
                then submit it for approval. Dibs fills the image per recipient (the customer's most relevant listing
                photo) — you don't pick one here. Until Meta approves the image version, win-backs keep sending as
                text-only.
              </p>
            )}
          </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm">Message body</Label>
            <div className="flex flex-wrap gap-1.5">
              {tokensFor(draft.purpose).map((t) => (
                <Button
                  key={t.key}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => insertToken(t.token)}
                >
                  + {t.label}
                </Button>
              ))}
            </div>
            <Textarea
              ref={bodyRef}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              rows={6}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              {draft.purpose === "workflow"
                ? "Tap a chip to drop in a detail filled per customer."
                : "Tap a chip to drop in a variable the AI fills per customer."}{" "}
              These become {"{{1}}, {{2}}…"} in the version you paste into Meta.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">
              Footer <span className="text-muted-foreground">— optional</span>
            </Label>
            <Input
              value={draft.footer_text}
              onChange={(e) => setDraft({ ...draft, footer_text: e.target.value })}
              placeholder="e.g. Reply STOP to opt out"
            />
          </div>

          {/* Preview */}
          <div className="rounded-lg bg-muted/50 p-3 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview (sample data)</p>
            <p className="text-sm text-foreground whitespace-pre-wrap break-words">{renderSample(draft.body, tokensFor(draft.purpose))}</p>
            {draft.footer_text.trim() && (
              <p className="text-xs text-muted-foreground border-t border-border pt-1.5">{draft.footer_text}</p>
            )}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-1">Paste into Meta</p>
            <p className="text-sm font-mono text-muted-foreground whitespace-pre-wrap break-words">
              {buildMeta(draft.body, tokensFor(draft.purpose)).metaBody}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={saveDraft} disabled={isSaving} className="min-w-[130px]">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : draft.id ? (
                "Save changes"
              ) : (
                "Save template"
              )}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="gap-2" onClick={() => setDraft(emptyDraft())}>
          <Plus className="h-4 w-4" />
          Add a template
        </Button>
      )}
    </div>
  );
};

export default WinbackTemplates;
